import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  TrophyIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { weeklyProgramsAPI } from '../../services/api'
import { useChartTheme } from '../../hooks/useChartTheme'

function TrendIcon({ trend }) {
  if (trend === 'up') return <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />
  if (trend === 'down') return <ArrowTrendingDownIcon className="h-4 w-4 text-red-600" />
  return <MinusIcon className="h-4 w-4 text-gray-400" />
}

function ScoreBadge({ score }) {
  if (score == null) return null
  const rounded = Math.round(Number(score))
  let bg = 'bg-gray-100 text-gray-700'
  if (rounded >= 80) bg = 'bg-green-100 text-green-700'
  else if (rounded >= 60) bg = 'bg-blue-100 text-blue-700'
  else if (rounded >= 40) bg = 'bg-amber-100 text-amber-700'
  else bg = 'bg-red-100 text-red-700'

  return (
    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${bg}`}>
      {rounded}
    </span>
  )
}

function ExerciseHistoryRow({ exerciseName }) {
  // VH-133: recharts takes colours as props, so it inherits nothing from
  // the design system — these follow the active theme instead.
  const chart = useChartTheme()
  const { t } = useTranslation()
  const [expandedDate, setExpandedDate] = useState(null)

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['exerciseHistory', exerciseName],
    queryFn: () => weeklyProgramsAPI.getExerciseHistory(exerciseName),
    select: (res) => res.data,
  })

  if (isLoading) {
    return <div className="px-4 py-3 animate-pulse"><div className="h-16 bg-gray-100 rounded"></div></div>
  }

  const entries = historyData?.entries || []
  if (entries.length === 0) {
    return (
      <div className="px-4 py-2 text-sm text-gray-400">
        {t('exerciseProgress.noHistory', 'No historical data available')}
      </div>
    )
  }

  // Score trend chart data (chronological order)
  const entriesWithScore = entries.filter(e => e.score != null)
  const chartData = [...entriesWithScore].reverse().map((e) => ({
    date: new Date(e.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    score: Math.round(Number(e.score)),
  }))

  return (
    <div className="px-4 pb-3">
      {/* Score trend mini-chart */}
      {chartData.length >= 2 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 mb-1">
            {t('exerciseProgress.scoreTrend', 'Score Trend')}
          </p>
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: chart.axis }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: chart.axis }} width={30} />
                <Tooltip {...chart.tooltipProps} />
                <Line
                  type="linear"
                  dataKey="score"
                  stroke={chart.primary}
                  strokeWidth={2}
                  dot={{ r: 2, fill: '#4F46E5' }}
                  name={t('exerciseProgress.score', 'Score')}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* History table */}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-500 border-b">
            <th className="text-left py-1 font-medium">{t('common.date', 'Date')}</th>
            <th className="text-right py-1 font-medium">{t('exerciseProgress.score', 'Score')}</th>
            <th className="text-right py-1 font-medium">{t('exerciseProgress.weightKg', 'Weight (kg)')}</th>
            <th className="text-right py-1 font-medium">{t('exerciseProgress.reps', 'Reps')}</th>
            <th className="text-right py-1 font-medium">{t('exerciseProgress.estimated1RM', 'Est. 1RM')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, idx) => {
            const dateKey = `${entry.date}-${entry.session_id}`
            const isRowExpanded = expandedDate === dateKey
            const hasSets = entry.sets_detail && entry.sets_detail.length > 0

            return (
              <tr key={idx} className="border-b border-gray-50 group" style={{ display: 'table-row' }}>
                <td colSpan={5} className="p-0">
                  <div>
                    <button
                      onClick={() => hasSets && setExpandedDate(isRowExpanded ? null : dateKey)}
                      className={`w-full flex items-center text-left ${hasSets ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                      disabled={!hasSets}
                    >
                      <span className="py-1.5 text-gray-700 flex-1">
                        {hasSets && (
                          isRowExpanded
                            ? <ChevronUpIcon className="h-3 w-3 inline mr-1 text-gray-400" />
                            : <ChevronDownIcon className="h-3 w-3 inline mr-1 text-gray-400" />
                        )}
                        {new Date(entry.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}
                      </span>
                      <span className="py-1.5 text-right w-14">
                        <ScoreBadge score={entry.score} />
                      </span>
                      <span className="py-1.5 text-right font-medium text-gray-900 w-20">
                        {entry.weight_kg != null ? entry.weight_kg : '—'}
                      </span>
                      <span className="py-1.5 text-right text-gray-600 w-12">
                        {entry.reps ?? '—'}
                      </span>
                      <span className="py-1.5 text-right text-gray-600 w-16">
                        {entry.estimated_1rm != null ? Number(entry.estimated_1rm).toFixed(1) : '—'}
                      </span>
                    </button>

                    {/* Expanded set details */}
                    {isRowExpanded && hasSets && (
                      <div className="bg-gray-50 px-4 py-2 mb-1 rounded">
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          {t('exerciseProgress.setDetails', 'Set Details')}
                        </p>
                        <div className="space-y-0.5">
                          {entry.sets_detail.map((s, sIdx) => (
                            <div key={sIdx} className="flex items-center text-xs text-gray-600">
                              <span className="w-16 text-gray-400">
                                {s.set_type === 'warmup' ? 'Warmup' : `Set ${s.set_number}`}
                              </span>
                              <span className="w-16 font-medium text-gray-700">
                                {s.weight_kg != null ? `${s.weight_kg} kg` : '—'}
                              </span>
                              <span className="w-12">
                                {s.reps != null ? `${s.reps} reps` : '—'}
                              </span>
                              {s.is_new_pb && (
                                <TrophyIcon className="h-3 w-3 text-amber-500 ml-1" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function PerExerciseView() {
  const { t } = useTranslation()
  const [expandedExercise, setExpandedExercise] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const { data: summaryData, isLoading } = useQuery({
    queryKey: ['exerciseSummary'],
    queryFn: () => weeklyProgramsAPI.getExerciseSummary(),
    select: (res) => res.data,
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const exercises = summaryData?.exercises || []
  if (exercises.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-8">
          <TrophyIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">{t('exerciseProgress.noExerciseData', 'No exercise data yet')}</p>
          <p className="text-sm text-gray-400 mt-1">{t('exerciseProgress.noExerciseDataHint', 'Complete workout sessions to see per-exercise progress')}</p>
        </div>
      </div>
    )
  }

  const filtered = exercises.filter((ex) =>
    ex.exercise_name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('exerciseProgress.searchExercises', 'Search exercises...')}
          className="input w-full pl-9 text-sm"
        />
      </div>

      {/* Exercise list */}
      {filtered.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">{t('exerciseProgress.noMatchingExercises', 'No matching exercises')}</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((ex) => {
            const isExpanded = expandedExercise === ex.exercise_name

            return (
              <div key={ex.exercise_name} className="card overflow-hidden">
                <button
                  onClick={() => setExpandedExercise(isExpanded ? null : ex.exercise_name)}
                  className="w-full card-body hover:bg-gray-50 flex items-center justify-between text-left"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{ex.exercise_name}</p>
                    <p className="text-xs text-gray-400">
                      {ex.total_sessions} {t('exerciseProgress.sessions', 'sessions')}
                      {ex.latest_date && (
                        <> · {t('exerciseProgress.lastPerformed', 'Last')}: {new Date(ex.latest_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      {ex.latest_weight != null && (
                        <p className="text-sm font-bold text-gray-900">{ex.latest_weight} kg</p>
                      )}
                      {ex.latest_1rm != null && (
                        <p className="text-xs text-gray-500">1RM: {Number(ex.latest_1rm).toFixed(1)} kg</p>
                      )}
                    </div>
                    <TrendIcon trend={ex.trend} />
                    {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t">
                    <ExerciseHistoryRow exerciseName={ex.exercise_name} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
